import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { ContentModel, PaginationDataModel } from 'src/app/models/common.model';
import { SharesTransactionModel, TotalSharesModel } from 'src/app/models/shares.model';
import { StorageModel } from 'src/app/models/storage.model';
import { TrashModel } from 'src/app/models/trash.model';
import { VersionListModel } from 'src/app/models/version.model';

@Injectable({
  providedIn: 'root',
})
export class PaginationService {
  readonly DEFAULT_STORAGE_FILES_PAGESIZE = 20;
  readonly DEFAULT_STORAGE_FOLDERS_PAGESIZE = 8;
  readonly DEFAULT_STORAGE_LISTVIEW_PAGESIZE = 30;
  readonly DEFAULT_LOAD_MORE_PAGESIZE = 20;
  readonly DEAFULT_TRASHED_ITEMS_PAGESIZE = 30;
  readonly DEFAULT_VERSION_LIST_PAGESIZE = 15;
  defaultStorageFiles = { items: [], totalCount: 0, pageSize: 20, pageNumber: 0 };
  defaultStorageFolders = { items: [], totalCount: 0, pageSize: 8, pageNumber: 0 };
  defaultStorageFilesAndFolders = { items: [], totalCount: 0, pageSize: 30, pageNumber: 0 };

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  private pageNumber = new Subject<number>();
  pageNumber$ = this.pageNumber.asObservable();

  private storageFiles = new BehaviorSubject<ContentModel<StorageModel[]>>(this.defaultStorageFiles);
  storageFiles$ = this.storageFiles.asObservable();

  private versionFiles = new BehaviorSubject<ContentModel<VersionListModel[]>>(this.defaultStorageFiles);
  versionFiles$ = this.versionFiles.asObservable();

  private storageFolders = new BehaviorSubject<ContentModel<StorageModel[]>>(this.defaultStorageFolders);
  storageFolders$ = this.storageFolders.asObservable();

  private storageFilesAndFolders = new BehaviorSubject<ContentModel<StorageModel[]>>(this.defaultStorageFilesAndFolders);
  storageFilesAndFolders$ = this.storageFilesAndFolders.asObservable();

  private sharesFiles = new BehaviorSubject<ContentModel<TotalSharesModel[]>>(this.defaultStorageFiles);
  sharesFiles$ = this.sharesFiles.asObservable();

  private sharesFolders = new BehaviorSubject<ContentModel<TotalSharesModel[]>>(this.defaultStorageFolders);
  sharesFolders$ = this.sharesFolders.asObservable();

  private sharesFilesAndFolders = new BehaviorSubject<ContentModel<TotalSharesModel[]>>(this.defaultStorageFilesAndFolders);
  sharesFilesAndFolders$ = this.sharesFilesAndFolders.asObservable();

  private transactionFilesAndFolders = new BehaviorSubject<ContentModel<SharesTransactionModel[]>>(this.defaultStorageFilesAndFolders);
  transactionFilesAndFolder$ = this.sharesFilesAndFolders.asObservable();

  private trashedItems = new BehaviorSubject<ContentModel<TrashModel[]>>(this.defaultStorageFilesAndFolders);
  trashedItems$ = this.trashedItems.asObservable();

  private paginationDataSubject = new Subject<PaginationDataModel>();
  paginationData$ = this.paginationDataSubject.asObservable();

  private resetPagination = new Subject<boolean>();
  resetPagination$ = this.resetPagination.asObservable();

  currentPageNumber = 1;
  isLoading = false;
  paginationData!: PaginationDataModel;

  subscription: Subscription = new Subscription();

  constructor() {
    this.subscription.add(
      this.paginationData$.subscribe((data) => {
        this.paginationData = data;
      }),
    );
    this.subscription.add(
      this.isLoading$.subscribe((isLoading) => {
        this.isLoading = isLoading;
      }),
    );
    this.subscription.add(
      this.resetPagination$.subscribe(() => {
        this.currentPageNumber = 1;
      }),
    );
  }

  onScroll(event: Event): void {
    const container = event.target as HTMLElement;
    const { scrollHeight } = container;
    const { scrollTop } = container;
    const { clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      this.loadItems();
      event.preventDefault();
    }
  }

  loadItems(): void {
    if (this.isLoading) {
      return;
    }
    if (this.paginationData && this.paginationData.size >= this.paginationData.total) {
      return;
    }
    this.setPageNumber(this.currentPageNumber);
    this.currentPageNumber++;
  }

  setLoadingState(isLoading: boolean): void {
    this.isLoadingSubject.next(isLoading);
  }

  setPageNumber(pageNumber: number): void {
    this.pageNumber.next(pageNumber);
  }

  setStorageFiles(storageFiles: ContentModel<StorageModel[]>): void {
    this.storageFiles.next(storageFiles);
  }

  setVersionFiles(versionFiles: ContentModel<VersionListModel[]>): void {
    this.versionFiles.next(versionFiles);
  }

  setStorageFolders(storageFolders: ContentModel<StorageModel[]>): void {
    this.storageFolders.next(storageFolders);
  }

  setStorageFilesAndFolders(storageFilesAndFolders: ContentModel<StorageModel[]>): void {
    this.storageFilesAndFolders.next(storageFilesAndFolders);
  }

  setSharesFiles(sharesFiles: ContentModel<TotalSharesModel[]>): void {
    this.sharesFiles.next(sharesFiles);
  }

  setSharesFolders(sharesFolders: ContentModel<TotalSharesModel[]>): void {
    this.sharesFolders.next(sharesFolders);
  }

  setSharesFilesAndFolders(sharesFilesAndFolders: ContentModel<TotalSharesModel[]>): void {
    this.sharesFilesAndFolders.next(sharesFilesAndFolders);
  }

  setSharesTransactionFilesAndFolders(transactionFilesAndFolders: ContentModel<SharesTransactionModel[]>): void {
    this.transactionFilesAndFolders.next(transactionFilesAndFolders);
  }

  setTrashedItems(trashedItems: ContentModel<TrashModel[]>): void {
    this.trashedItems.next(trashedItems);
  }

  setTotalCount(data: PaginationDataModel): void {
    this.paginationDataSubject.next(data);
  }

  reset(): void {
    this.resetPagination.next(true);
  }
}
